import { StateCreator } from "zustand";
import { Store } from "./types";
import { generateChatResponse as generateChatResponseAPI } from "@/lib/api/chat";
import type { ChatResponse } from "@/lib/api/types";

/**
 * Show `text` to the user as an assistant turn.
 *
 * Used for failures that have no server-side message to attach to, so the reply is
 * visible in the transcript rather than only in the console.
 */
const appendAssistantText = (
  set: (fn: (state: Store) => Partial<Store>) => void,
  text: string
) => {
  set((state) => ({
    messages: [
      ...state.messages,
      {
        id: `error-${Date.now()}`,
        role: "assistant",
        created_at: new Date().toISOString(),
        content: { text, image: null, citations: [] },
      },
    ],
  }));
};

export const createChatSlice: StateCreator<
  Store,
  [],
  [],
  Pick<
    Store,
    | "messages"
    | "sessionId"
    | "isLoadedFromHistory"
    | "clearChat"
    | "submitMessage"
  >
> = (set, get) => ({
  messages: [],
  sessionId: null,
  isLoadedFromHistory: false,
  clearChat: () => {
    // A cleared chat is a new conversation, so it is eligible for the greeting
    // again. `loadChatHistory` calls this first and then sets the flag back.
    set({ messages: [], sessionId: null, isLoadedFromHistory: false });
  },
  submitMessage: (reqMessage) => {
    set((state) => ({ messages: [...state.messages, reqMessage] }));

    const allMessages = get().messages.map((message) => ({
      content: {
        ...message.content,
        text: message.content.text,
        image: message.content.image ?? null,
      },
      role: message.role,
      created_at: message.created_at,
      id: message.id,
    }));

    const handleStreamMessage = (chunk: string) => {
      let parsedChunk: ChatResponse;
      try {
        parsedChunk = JSON.parse(chunk) as ChatResponse;
      } catch {
        console.error("Discarding unparseable chat chunk");
        return;
      }

      // A mid-stream failure arrives as a terminal {"error": ...} chunk, which has
      // no history_metadata and no choices. Reading through it threw a TypeError
      // inside the stream loop, so the turn ended with nothing rendered at all —
      // every backend 500 and every content-filter rejection looked to the user
      // like the bot had simply gone silent.
      if (parsedChunk.error) {
        console.error("Chat stream reported an error:", parsedChunk.error);
        appendAssistantText(
          set,
          "Sorry, er is iets misgegaan bij het ophalen van een antwoord. Probeer het opnieuw."
        );
        return;
      }

      if (parsedChunk.history_metadata.session_id && !get().sessionId) {
        set({ sessionId: parsedChunk.history_metadata.session_id });
        get().fetchSessionList();
      }

      const resMessage = parsedChunk.choices[0].messages[0];

      if (!get().messages.find((x) => x.id === resMessage.id)) {
        // The wire puts citations at the top level of the message, but everything
        // downstream (the merge below, Message.tsx, historyState) reads
        // content.citations - so normalise on insert instead of leaving the first
        // payload's list somewhere nothing looks.
        set((state) => ({
          messages: [
            ...state.messages,
            {
              ...resMessage,
              content: {
                ...resMessage.content,
                citations: resMessage.citations ?? [],
              },
            },
          ],
        }));
      } else {
        set((state) => ({
          messages: state.messages.map((current) =>
            current.id === resMessage.id
              ? {
                  ...current,
                  content: {
                    text: current.content.text + resMessage.content.text,
                    image: resMessage.content.image ?? null,
                    // Text accumulates, citations do NOT: the server sends the
                    // whole cited-source list again whenever it grows, so an
                    // incoming array is authoritative and replaces what we hold.
                    // An empty array always means "unchanged" — the list only
                    // ever grows within a message, so it can never mean "cleared".
                    citations: resMessage.citations?.length
                      ? resMessage.citations
                      : current.content.citations ?? [],
                  },
                }
              : current
          ),
        }));
      }
    };

    generateChatResponseAPI(allMessages, get().sessionId, handleStreamMessage);
  },
});
